import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, X, Download, User } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';

interface Document {
  id: string;
  title: string;
  description: string;
  category: 'onboarding' | 'resources' | 'policies';
  file_url: string;
  file_name: string;
  file_size: number;
  posted_by_type: 'admin' | 'client';
  posted_by_name: string;
  consultant_id: string | null;
  consultant_name?: string;
  created_at: string;
}

interface Consultant {
  id: string;
  name: string;
  email: string;
}

const CATEGORY_LABELS = {
  onboarding: 'Onboarding',
  resources: 'Resources',
  training: 'Training',
  policies: 'Policies'
};

interface ConsultantDocumentUploadProps {
  category?: 'onboarding' | 'training' | 'policies';
}

export default function ConsultantDocumentUpload({ category = 'onboarding' }: ConsultantDocumentUploadProps) {
  const { user } = useAuth();
  const { theme } = useWorkdayClientTheme();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loadingConsultants, setLoadingConsultants] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    consultant_id: '' as string,
    file: null as File | null
  });

  useEffect(() => {
    fetchUserProfile();
    fetchDocuments();
    fetchHiredConsultants();
  }, [category]);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('workday_client_profiles')
        .select('first_name, last_name, companies(name)')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const company = (data as any)?.companies?.name || '';
      setCompanyName(company || `${data?.first_name} ${data?.last_name}`);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('consultant_documents')
        .select(`
          *,
          consultant_profiles:consultant_id(first_name, last_name)
        `)
        .eq('category', category === 'training' ? 'resources' : category)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const documentsWithConsultantNames = (data || []).map((doc: any) => ({
        ...doc,
        consultant_name: doc.consultant_profiles
          ? `${doc.consultant_profiles.first_name} ${doc.consultant_profiles.last_name}`
          : null
      }));

      setDocuments(documentsWithConsultantNames);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHiredConsultants = async () => {
    if (!user) return;

    setLoadingConsultants(true);
    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select(`
          consultant_id,
          consultant_profiles!inner(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('client_id', user.id)
        .eq('status', 'approved');

      if (error) throw error;

      const uniqueConsultants = Array.from(
        new Map(
          (data || []).map((item: any) => [
            item.consultant_id,
            {
              id: item.consultant_id,
              name: `${item.consultant_profiles.first_name} ${item.consultant_profiles.last_name}`,
              email: item.consultant_profiles.email
            }
          ])
        ).values()
      );

      setConsultants(uniqueConsultants);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    } finally {
      setLoadingConsultants(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, file });
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file || !user) return;

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const fileExt = formData.file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('consultant-documents')
        .upload(fileName, formData.file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('consultant-documents')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from('consultant_documents')
        .insert({
          title: formData.title,
          description: formData.description,
          category: category === 'training' ? 'resources' : category,
          file_url: publicUrl,
          file_name: formData.file.name,
          file_size: formData.file.size,
          posted_by_type: 'client',
          posted_by_id: user.id,
          posted_by_name: companyName,
          client_id: user.id,
          consultant_id: formData.consultant_id || null
        });

      if (insertError) throw insertError;

      setFormData({ title: '', description: '', consultant_id: '', file: null });
      setUploadSuccess(true);

      setTimeout(() => {
        setShowUploadModal(false);
        setUploadSuccess(false);
      }, 1500);

      fetchDocuments();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      setUploadError(error.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId: string, fileUrl: string) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('consultant_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      const urlParts = fileUrl.split('/consultant-documents/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        await supabase.storage
          .from('consultant-documents')
          .remove([filePath]);
      }

      fetchDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document: ' + (error.message || 'Unknown error'));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {category === 'onboarding' ? 'Onboarding Documents' : category === 'training' ? 'Training & Resources' : 'Policy Documents'}
        </h2>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors font-semibold"
        >
          <Upload className="w-5 h-5" />
          <span>Upload Document</span>
        </button>
      </div>

      <div className={`rounded-lg p-6 ${
        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
      }`}>
        <div className="space-y-3">
          {documents.length > 0 ? (
            documents.map((doc) => (
              <div
                key={doc.id}
                className={`p-4 rounded-lg border ${
                  theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start space-x-3 flex-1">
                    <FileText className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold break-words ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {doc.title}
                      </h4>
                      {doc.description && (
                        <p className={`text-sm mt-1 break-words ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {doc.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                        <span className={`px-2 py-1 rounded font-semibold ${
                          doc.posted_by_type === 'admin'
                            ? 'bg-blue-500/20 text-blue-600'
                            : 'bg-green-500/20 text-green-600'
                        }`}>
                          {doc.posted_by_type === 'admin' ? 'Perplexity Admin' : doc.posted_by_name}
                        </span>
                        {doc.consultant_id && doc.consultant_name ? (
                          <span className="px-2 py-1 rounded font-semibold bg-[#40E0D0]/20 text-[#40E0D0] flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {doc.consultant_name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded font-semibold bg-gray-500/20 text-gray-600">
                            All Consultants
                          </span>
                        )}
                        <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                          {formatFileSize(doc.file_size)}
                        </span>
                        <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                          {new Date(doc.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    <a
                      href={doc.file_url}
                      download
                      className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark' ? 'text-gray-400 hover:text-[#40E0D0] hover:bg-gray-600' : 'text-gray-600 hover:text-[#40E0D0] hover:bg-gray-100'
                      }`}
                      title="Download"
                    >
                      <Download className="w-5 h-5" />
                    </a>
                    {doc.posted_by_id === user?.id && (
                      <button
                        onClick={() => handleDelete(doc.id, doc.file_url)}
                        className={`p-2 rounded-lg transition-colors ${
                          theme === 'dark' ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-600 hover:text-red-600 hover:bg-gray-100'
                        }`}
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <FileText className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                No documents uploaded yet
              </h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                Click the "Upload Document" button to share {category === 'onboarding' ? 'onboarding materials' : category === 'training' ? 'training resources' : 'company policies'} with consultants
              </p>
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Upload Document
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              {uploadSuccess && (
                <div className="p-3 rounded-lg bg-green-500/20 border border-green-500/50">
                  <p className="text-sm text-green-600 font-semibold">Document uploaded successfully!</p>
                </div>
              )}

              {uploadError && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/50">
                  <p className="text-sm text-red-600 font-semibold">{uploadError}</p>
                </div>
              )}

              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Uploading to: <span className="font-semibold text-[#40E0D0]">
                    {category === 'onboarding' ? 'Onboarding' : category === 'training' ? 'Training & Resources' : 'Policies'}
                  </span>
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Assign to Consultant
                </label>
                <select
                  value={formData.consultant_id}
                  onChange={(e) => setFormData({ ...formData, consultant_id: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={loadingConsultants}
                >
                  <option value="">All Consultants (Shared)</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.name} ({consultant.email})
                    </option>
                  ))}
                </select>
                {loadingConsultants && (
                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Loading your hired consultants...
                  </p>
                )}
                {!loadingConsultants && consultants.length === 0 && (
                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    No approved consultants found. Document will be shared with all consultants.
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Consultant Handbook, Security Policy"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the document contents..."
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  rows={3}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  File
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.jpg,.jpeg,.png,.mp4,.mov"
                  required
                />
                {formData.file && (
                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Selected: {formData.file.name} ({formatFileSize(formData.file.size)})
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setFormData({ title: '', description: '', consultant_id: '', file: null });
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  }`}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Document
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
